import React, { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, User, Users, StickyNote, Navigation, XCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useTheme, ThemeToggle } from "../components/ThemeToggle";
import { SafetyActions } from "../components/SafetyActions";
import { RideMap } from "../components/RideMap";
import { ChatPanel } from "../components/ChatPanel";
import { SlideToConfirm } from "../components/SlideToConfirm";

const tripBadge = {
  assigned: "text-amber-400 border-amber-400/40",
  on_the_way: "text-sky-400 border-sky-400/40",
  completed: "text-emerald-400 border-emerald-400/40",
};

export default function DriverPortal() {
  const [phone, setPhone] = useState(localStorage.getItem("nl_driver_phone") || "");
  const [trips, setTrips] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sharingId, setSharingId] = useState(null);
  const [myPos, setMyPos] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [cancelTrip_, setCancelTrip_] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const watchRef = useRef(null);

  const [theme, toggleTheme] = useTheme("night");

  useEffect(() => { api.get("/cancellation-reasons").then((r) => setReasons(r.data.staff || [])).catch(() => {}); }, []);

  // Track the driver's own position (for the in-app map) whenever there's an active trip.
  useEffect(() => {
    const active = (trips || []).some((t) => t.trip_status !== "completed");
    if (!active || !navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (p) => setMyPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [trips]);

  const fetchTrips = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/driver/trips`, { params: { phone: p } });
      setTrips(data);
      localStorage.setItem("nl_driver_phone", p);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load trips");
    } finally {
      setLoading(false);
    }
  }, []);

  const submit = (e) => { e.preventDefault(); if (phone.trim()) fetchTrips(phone.trim()); };

  const setStatus = async (id, status) => {
    const msg = { on_the_way: "You're on the way", arrived: "Marked arrived at pickup", in_progress: "Ride started", completed: "Trip completed" }[status] || "Updated";
    try {
      await api.post(`/driver/trips/${id}/status`, null, { params: { phone: phone.trim(), status } });
      toast.success(msg);
      fetchTrips(phone.trim());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const cancelTrip = async () => {
    if (!cancelReason) { toast.error("Please choose a reason"); return; }
    setCancelling(true);
    try {
      await api.post(`/driver/trips/${cancelTrip_.id}/cancel`, { reason: cancelReason, phone: phone.trim() });
      toast.success("Trip cancelled. The client has been notified.");
      setCancelTrip_(null); setCancelReason("");
      fetchTrips(phone.trim());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not cancel");
    } finally {
      setCancelling(false);
    }
  };

  const toggleShare = (id) => {
    if (sharingId === id) {
      if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
      setSharingId(null);
      toast.info("Stopped sharing location");
      return;
    }
    if (!navigator.geolocation) { toast.error("Location not supported on this device"); return; }
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setMyPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        api.post(`/driver/trips/${id}/location`, { phone: phone.trim(), lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {});
      },
      () => toast.error("Couldn't get location — check permissions"),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
    setSharingId(id);
    toast.success("Sharing live location with the client");
  };

  // Navigation launchers — driver picks whichever maps app they prefer.
  const navTargets = (t) => {
    const dest = t.pickup_lat != null && t.pickup_lng != null
      ? `${t.pickup_lat},${t.pickup_lng}`
      : encodeURIComponent(t.pickup);
    return {
      google: `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`,
      waze: t.pickup_lat != null ? `https://waze.com/ul?ll=${t.pickup_lat},${t.pickup_lng}&navigate=yes` : `https://waze.com/ul?q=${encodeURIComponent(t.pickup)}&navigate=yes`,
      apple: `https://maps.apple.com/?daddr=${dest}&dirflg=d`,
    };
  };

  return (
    <div className="min-h-screen grain relative bg-[var(--nl-bg)] px-6 py-10" data-theme={theme} data-testid="driver-portal">
      <ThemeToggle theme={theme} onToggle={toggleTheme} />
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="flex items-baseline gap-1 mb-2 justify-center">
          <span className="font-serif text-2xl">Namarck</span>
          <span className="font-serif text-2xl italic text-gold">Limo</span>
        </Link>
        <p className="label text-gold text-center mb-8">Chauffeur Portal</p>

        <form onSubmit={submit} className="flex gap-3 mb-10" data-testid="driver-phone-form">
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Enter your phone number"
            className="flex-1 bg-[var(--nl-bg-3)] border border-white/10 px-4 py-3 text-white font-body focus:outline-none focus:border-gold transition-colors duration-300"
            data-testid="driver-phone-input"
          />
          <button type="submit" disabled={loading} className="label bg-gold text-black px-6 hover:bg-[var(--nl-gold-hover)] transition-colors duration-300 disabled:opacity-60" data-testid="driver-load-btn">
            {loading ? "…" : "View Trips"}
          </button>
        </form>

        {trips !== null && trips.length === 0 && (
          <p className="text-white/40 font-body text-center py-10" data-testid="driver-no-trips">No trips assigned to this number.</p>
        )}

        <div className="space-y-4">
          {(trips || []).map((t) => {
            const nav = navTargets(t);
            return (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-white/10 bg-[var(--nl-bg-2)] p-6"
              data-testid={`driver-trip-${t.id}`}
            >
              <div className="flex items-start justify-between mb-4 gap-4">
                <div>
                  <p className="font-serif text-2xl">{t.date} · {t.time}</p>
                  <p className="text-white/50 font-body text-sm">{t.vehicle} {t.mode === "hourly" ? `· ${t.hours}h charter` : ""}</p>
                </div>
                <span className={`border px-3 py-1 label ${tripBadge[t.trip_status] || "text-white/50 border-white/20"}`}>
                  {(t.trip_status || "assigned").replace("_", " ")}
                </span>
              </div>
              <div className="space-y-2 font-body text-sm text-white/70 border-t border-white/10 pt-4">
                <p className="flex items-center gap-2"><MapPin size={15} className="text-gold" /> <span className="text-gold">Pickup:</span> {t.pickup}</p>
                <p className="flex items-center gap-2"><MapPin size={15} className="text-white/40" /> <span className="text-white/40">Drop-off:</span> {t.dropoff}</p>
                <p className="flex items-center gap-2"><User size={15} className="text-gold" /> {t.name} — {t.phone}</p>
                <p className="flex items-center gap-2"><Users size={15} className="text-white/40" /> {t.passengers} passenger(s)</p>
                {t.notes && <p className="flex items-start gap-2"><StickyNote size={15} className="text-white/40 mt-0.5" /> {t.notes}</p>}
              </div>

              {t.trip_status !== "completed" && t.pickup_lat != null && (
                <div className="mt-4" data-testid={`driver-map-${t.id}`}>
                  <RideMap
                    theme={theme}
                    phase="route"
                    pickup={{ lat: t.pickup_lat, lng: t.pickup_lng, name: t.pickup }}
                    dropoff={{ lat: t.dropoff_lat, lng: t.dropoff_lng, name: t.dropoff }}
                    car={myPos}
                    className="h-72"
                    info={{ rideName: t.vehicle, driverName: t.name, plate: t.phone, statusText: (t.trip_status || "assigned").replace("_", " "), passengers: t.passengers, notes: t.notes }}
                  />
                </div>
              )}

              {t.trip_status !== "completed" && (
                <div className="mt-4" data-testid={`driver-chat-${t.id}`}>
                  <ChatPanel
                    me="driver"
                    title={`Chat with ${t.name || "rider"}`}
                    listUrl={`/driver/trips/${t.id}/chat?phone=${encodeURIComponent(phone.trim())}`}
                    sendUrl={`/driver/trips/${t.id}/chat`}
                    sendBase={{ phone: phone.trim() }}
                    quickReplies={["On my way", "I'm outside", "Running 5 min late", "I've arrived"]}
                    testPrefix={`driver-chat-${t.id}`}
                  />
                </div>
              )}

              {/* Navigation — built-in map above, or open in the driver's preferred app */}
              <div className="mt-3">
                <p className="label text-white/40 text-[10px] mb-1.5">Navigate to pickup in</p>
                <div className="flex gap-2">
                  <a href={nav.google} target="_blank" rel="noopener noreferrer" className="flex-1 label text-xs border border-gold/40 text-gold py-2.5 flex items-center justify-center gap-1.5 hover:bg-gold/10 transition-colors duration-300" data-testid={`nav-google-${t.id}`}>
                    <Navigation size={13} /> Google
                  </a>
                  <a href={nav.waze} target="_blank" rel="noopener noreferrer" className="flex-1 label text-xs border border-sky-400/40 text-sky-400 py-2.5 flex items-center justify-center gap-1.5 hover:bg-sky-400/10 transition-colors duration-300" data-testid={`nav-waze-${t.id}`}>
                    <Navigation size={13} /> Waze
                  </a>
                  <a href={nav.apple} target="_blank" rel="noopener noreferrer" className="flex-1 label text-xs border border-white/20 text-white/70 py-2.5 flex items-center justify-center gap-1.5 hover:bg-white/5 transition-colors duration-300" data-testid={`nav-apple-${t.id}`}>
                    <Navigation size={13} /> Apple
                  </a>
                </div>
              </div>

              {t.trip_status !== "completed" && (
                <div className="mt-5 space-y-3" data-testid={`driver-actions-${t.id}`}>
                  {(t.trip_status === "assigned" || !t.trip_status) && (
                    <SlideToConfirm tone="sky" label="Slide — On my way to pickup" onConfirm={() => setStatus(t.id, "on_the_way")} testId={`slide-onway-${t.id}`} />
                  )}
                  {t.trip_status === "on_the_way" && (
                    <SlideToConfirm tone="gold" label="Slide — Arrived at pickup" onConfirm={() => setStatus(t.id, "arrived")} testId={`slide-pickup-${t.id}`} />
                  )}
                  {t.trip_status === "arrived" && (
                    <SlideToConfirm tone="emerald" label="Slide — Start ride" onConfirm={() => setStatus(t.id, "in_progress")} testId={`slide-start-${t.id}`} />
                  )}
                  {t.trip_status === "in_progress" && (
                    <SlideToConfirm tone="gold" label="Slide — Drop off &amp; complete" onConfirm={() => setStatus(t.id, "completed")} testId={`slide-dropoff-${t.id}`} />
                  )}
                </div>
              )}
              {["on_the_way", "arrived", "in_progress"].includes(t.trip_status) && (
                <button
                  onClick={() => toggleShare(t.id)}
                  className={`w-full mt-3 label py-3 flex items-center justify-center gap-2 border transition-colors duration-300 ${sharingId === t.id ? "bg-emerald-500/15 border-emerald-400/50 text-emerald-400" : "border-gold/40 text-gold hover:bg-gold/10"}`}
                  data-testid={`share-loc-${t.id}`}
                >
                  <Navigation size={15} /> {sharingId === t.id ? "Sharing live location — Stop" : "Share Live Location"}
                </button>
              )}
              {t.trip_status !== "completed" && (
                <button
                  onClick={() => { setCancelReason(""); setCancelTrip_(t); }}
                  className="w-full mt-3 label py-3 flex items-center justify-center gap-2 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors duration-300"
                  data-testid={`driver-cancel-${t.id}`}
                >
                  <XCircle size={15} /> Cancel trip
                </button>
              )}
              <SafetyActions idPayload={{ role: "driver", phone: phone.trim(), booking_id: t.id }} reportKind="found" testPrefix={`driver-${t.id}`} />
            </motion.div>
          );})}
        </div>
      </div>

      {cancelTrip_ && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setCancelTrip_(null)} data-testid="driver-cancel-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md border border-white/10 bg-[var(--nl-bg-2)] p-8 space-y-5">
            <div>
              <p className="label text-red-400 mb-1">Cancel Trip</p>
              <h3 className="font-serif font-light text-2xl text-white">Why are you cancelling?</h3>
              <p className="text-white/40 text-sm font-body mt-1">{cancelTrip_.date} {cancelTrip_.time} · {cancelTrip_.pickup} → {cancelTrip_.dropoff}</p>
            </div>
            <select value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="driver-cancel-reason">
              <option value="">Choose a reason…</option>
              {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <p className="text-white/35 text-xs font-body">The client will be texted &amp; push-notified. A no-show charges the client 100%; any other reason is free of charge.</p>
            <div className="flex gap-3">
              <button onClick={() => setCancelTrip_(null)} className="flex-1 label border border-white/15 text-white/60 py-3 hover:text-white transition-colors duration-300" data-testid="driver-cancel-dismiss">Keep trip</button>
              <button onClick={cancelTrip} disabled={cancelling} className="flex-1 label bg-red-500 text-white py-3 hover:bg-red-600 transition-colors duration-300 disabled:opacity-50" data-testid="driver-cancel-confirm">{cancelling ? "Cancelling…" : "Cancel trip"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
